import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Download, BookOpen } from "lucide-react";

const READING_TONE = {
  "very weak": "text-[#B8863C]",
  "weak": "text-[#B8863C]",
  "average": "text-[#78716C]",
  "solid": "text-[#1A1915]",
  "strong": "text-[#1A1915]",
};

function reading(v) {
  if (v < 2.0) return "very weak";
  if (v < 2.5) return "weak";
  if (v < 3.5) return "average";
  if (v <= 4.0) return "solid";
  return "strong";
}

export default function Results() {
  const { purchaseId } = useParams();
  const [searchParams] = useSearchParams();
  const isPrint = searchParams.get("print") === "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Signal print mode + a `ready` flag Playwright can wait on.
    if (isPrint) document.documentElement.classList.add("print-mode");
    return () => document.documentElement.classList.remove("print-mode");
  }, [isPrint]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: r } = await api.get(`/results/${purchaseId}`);
        if (!cancelled) setData(r);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || "Could not load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [purchaseId]);

  // Signal to a headless renderer (Playwright) that the page has fully painted.
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => {
      document.documentElement.setAttribute("data-report-ready", "1");
    }, 600); // let ResponsiveContainer + recharts complete their measure/paint cycle
    return () => clearTimeout(t);
  }, [data]);

  if (loading) return <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="results-loading"><div className="eyebrow">Loading report…</div></div>;
  if (error || !data) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="results-error">
      <div className="eyebrow">Error</div>
      <h1 className="mt-6 font-serif text-4xl text-[#1A1915]">{error || "Report not found."}</h1>
    </div>
  );

  const { scores, report, email, customer_name } = data;
  const displayName = (customer_name || "").trim() || null;
  const habitRadar = Object.entries(scores.habit_scores).map(([k, v]) => ({ dim: k, score: v }));
  const effectRadar = Object.entries(scores.effect_scores).map(([k, v]) => ({ dim: k, score: v }));
  const pdfUrl = `${API}/results/${purchaseId}/pdf`;

  // Shorten long labels only for the chart tick — keeps table copy intact.
  // Abbreviations are tight to keep the polar layout perfectly centered
  // (Recharts shifts the polar center when one side's labels are wider).
  const LABEL_SHORT = {
    "Appearance": "Appear.",
    "Relationships": "Relate",
    "Body Language": "Body Lang.",
    "Self-Assurance": "Assur.",
    "Congruence": "Congr.",
  };
  const renderAxisTick = ({ x, y, payload, textAnchor }) => (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor={textAnchor}
      fill="#1A1915"
      fontSize={11}
      fontFamily="Inter, sans-serif"
      fontWeight={500}
    >
      {LABEL_SHORT[payload.value] || payload.value}
    </text>
  );

  // Report metadata for the print cover page. `data.created_at` is ISO string.
  const reportDate = data.created_at ? new Date(data.created_at) : new Date();
  const issued = reportDate.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  const reportNo = `AOS-${purchaseId.slice(0, 8).toUpperCase()}`;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-16 lg:py-20" data-testid="results-page">
      {/* Print-only Cover Page — full-page dossier with recipient name + purchase ID */}
      {isPrint && (
        <section
          className="print-cover"
          data-testid="print-cover"
          style={{ pageBreakAfter: "always", breakAfter: "page" }}
        >
          {/* Masthead rule row */}
          <div className="flex items-center justify-between py-3 border-t-2 border-b border-[#1A1915] text-[10px] uppercase tracking-[0.25em] text-[#1A1915]">
            <span className="font-semibold">
              AuthorityOS <span className="text-[#78716C] mx-1">·</span> Diagnostic Report
            </span>
            <span className="text-[#B8863C] font-semibold">No.&nbsp;{reportNo}</span>
          </div>

          {/* Recipient — the visual centrepiece of the cover */}
          <div className="pt-24 pb-16 border-b border-[#1A1915]">
            <div className="eyebrow text-[#B8863C]">Prepared exclusively for</div>
            <div
              className="mt-8 font-serif text-[#1A1915] leading-[0.95] tracking-tight break-words"
              style={{ fontSize: displayName ? "5.5rem" : "3.5rem" }}
              data-testid="print-cover-name"
            >
              {displayName || email}
            </div>
            {displayName && (
              <div className="mt-6 text-base text-[#78716C] font-mono tracking-tight break-all">
                {email}
              </div>
            )}
          </div>

          {/* Verdict headline */}
          <div className="py-12 border-b border-[#1A1915]">
            <div className="eyebrow">Verdict</div>
            <h1 className="mt-4 font-serif text-4xl text-[#1A1915] leading-[1.05] max-w-3xl">
              {report.headline}
            </h1>
            <p className="mt-5 font-serif italic text-xl text-[#78716C] max-w-xl leading-snug">
              Read on for the full diagnosis and prescription.
            </p>
          </div>

          {/* Dossier metadata grid */}
          <div className="py-10 border-b border-[#1A1915]">
            <dl className="grid grid-cols-3 gap-y-6 gap-x-8 text-[13px]">
              <div>
                <dt className="eyebrow text-[#78716C]">Issued</dt>
                <dd className="mt-1 text-[#1A1915] font-medium">{issued}</dd>
              </div>
              <div>
                <dt className="eyebrow text-[#78716C]">Instrument</dt>
                <dd className="mt-1 text-[#1A1915] font-medium">v1.0</dd>
              </div>
              <div>
                <dt className="eyebrow text-[#78716C]">Model</dt>
                <dd className="mt-1 text-[#1A1915] font-medium">Claude Sonnet 5</dd>
              </div>
              <div className="col-span-2">
                <dt className="eyebrow text-[#78716C]">Purchase ID</dt>
                <dd
                  className="mt-1 text-[#1A1915] font-mono text-sm tracking-tight break-all"
                  data-testid="print-cover-purchase-id"
                >
                  {purchaseId}
                </dd>
              </div>
              <div>
                <dt className="eyebrow text-[#78716C]">Retention</dt>
                <dd className="mt-1 text-[#1A1915] font-medium">24 months</dd>
              </div>
            </dl>
          </div>

          {/* Foot rule */}
          <div className="flex items-center justify-between py-3 text-[10px] uppercase tracking-[0.25em] text-[#78716C]">
            <span>Confidential · Prepared solely for the named recipient</span>
            <span className="font-mono">authorityos.shop</span>
          </div>
        </section>
      )}

      {/* Header — hidden on print (cover replaces it) */}
      {!isPrint && (
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-[#1A1915] pb-8">
        <div>
          <div className="eyebrow">AuthorityOS Diagnostic Report</div>
          <h1 className="mt-4 font-serif text-5xl lg:text-6xl text-[#1A1915] leading-[0.95] max-w-3xl">{report.headline}</h1>
          <div className="mt-4 text-sm text-[#78716C]">Prepared for {email} · Purchase {purchaseId.slice(0, 8)}</div>
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer" className={`btn-primary ${isPrint ? "hidden" : ""}`} data-testid="results-download-pdf">
          <Download className="mr-2 w-4 h-4" strokeWidth={1.5} /> Download PDF
        </a>
      </div>
      )}

      {/* Composite score */}
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#1A1915] ${isPrint ? "" : "border-t-0"}`}>
        <div className="lg:col-span-5 p-8 lg:border-r border-[#1A1915]" data-testid="results-authority-score">
          <div className="eyebrow">Authority Score</div>
          <div className="font-serif text-[8rem] text-[#1A1915] leading-[1.15] mt-4 tracking-tight" data-testid="results-effect-score">
            {scores.authority_score.toFixed(1)}
          </div>
          <div className="text-sm text-[#78716C] mt-2">Composite / 5.0</div>
          <div className={`mt-2 text-xs uppercase tracking-widest font-semibold ${READING_TONE[scores.authority_reading]}`}>{scores.authority_reading}</div>
          <div className="mt-8 grid grid-cols-3 gap-4 text-xs">
            <div><div className="eyebrow">Habits</div><div className="font-serif text-3xl text-[#1A1915] mt-1">{scores.habit_mean.toFixed(1)}</div></div>
            <div><div className="eyebrow">Traits</div><div className="font-serif text-3xl text-[#1A1915] mt-1">{scores.trait_mean.toFixed(1)}</div></div>
            <div><div className="eyebrow">Effect</div><div className="font-serif text-3xl text-[#1A1915] mt-1">{scores.effect_mean.toFixed(1)}</div></div>
          </div>
        </div>
        {/* Bottleneck card */}
        <div className="lg:col-span-7 border-t-4 border-[#B8863C] p-8 lg:border-t-4 lg:border-l border-[#1A1915]" data-testid="results-bottleneck-card">
          <div className="eyebrow text-[#B8863C]">Bottleneck Identified</div>
          <div className="font-serif text-5xl text-[#1A1915] leading-tight mt-3" data-testid="results-bottleneck-dimension">{scores.bottleneck.zone}</div>
          <div className="text-sm text-[#78716C] mt-1">Habit zone · the causal lever</div>
          <div className="font-serif text-7xl text-[#B8863C] leading-none mt-4">{scores.bottleneck.score.toFixed(1)}<span className="text-2xl text-[#1A1915]"> / 5.0</span></div>
          <div className={`mt-2 text-xs uppercase tracking-widest font-semibold ${READING_TONE[scores.bottleneck.reading]}`}>{scores.bottleneck.reading}</div>
          <p className="mt-6 text-base text-[#1A1915] leading-relaxed max-w-lg">
            Habits are the causal lever. Every gain in traits and effect is throttled by this zone. Address it first — the model is explicit: Space → Structure → Style → Relationships → Finances. Do not skip.
          </p>
        </div>
      </div>

      {/* Two radar charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-t-0 border-[#1A1915]">
        <div className="p-8 lg:border-r border-[#1A1915]" data-testid="results-radar-habits">
          <div className="eyebrow">Habit Zones</div>
          <div className="mt-4 mx-auto aspect-square w-full max-w-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={habitRadar} margin={{ top: 12, right: 56, bottom: 12, left: 56 }} outerRadius="74%">
                <PolarGrid stroke="#1A1915" strokeOpacity={0.2} />
                <PolarAngleAxis dataKey="dim" tick={renderAxisTick} />
                <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#78716C", fontSize: 10 }} axisLine={false} />
                <Radar dataKey="score" stroke="#1A1915" strokeWidth={1.5} fill="#1A1915" fillOpacity={0.08} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-8 border-t lg:border-t-0 border-[#1A1915]" data-testid="results-radar-effect">
          <div className="eyebrow">Effect Markers</div>
          <div className="mt-4 mx-auto aspect-square w-full max-w-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={effectRadar} margin={{ top: 12, right: 56, bottom: 12, left: 56 }} outerRadius="74%">
                <PolarGrid stroke="#1A1915" strokeOpacity={0.2} />
                <PolarAngleAxis dataKey="dim" tick={renderAxisTick} />
                <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#78716C", fontSize: 10 }} axisLine={false} />
                <Radar dataKey="score" stroke="#B8863C" strokeWidth={1.5} fill="#B8863C" fillOpacity={0.06} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Score matrix */}
      <div className="mt-0 border border-t-0 border-[#1A1915]" data-testid="results-score-matrix">
        <div className="grid grid-cols-1 md:grid-cols-3">
          {[
            { label: "Habits", data: scores.habit_scores, highlight: scores.bottleneck.zone },
            { label: "Traits", data: scores.trait_scores },
            { label: "Effect", data: scores.effect_scores },
          ].map((col, i) => (
            <div key={col.label} className={`p-6 ${i < 2 ? "md:border-r border-[#1A1915]" : ""} ${i > 0 ? "border-t md:border-t-0 border-[#1A1915]" : ""}`}>
              <div className="eyebrow">{col.label}</div>
              <ul className="mt-4 divide-y divide-[#1A1915]/15">
                {Object.entries(col.data).map(([k, v]) => {
                  const isBottleneck = k === col.highlight;
                  return (
                    <li key={k} className="py-3 flex items-center justify-between">
                      <span className={`text-sm ${isBottleneck ? "text-[#B8863C] font-semibold" : "text-[#1A1915]"}`}>{k}</span>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs uppercase tracking-widest ${READING_TONE[reading(v)]}`}>{reading(v)}</span>
                        <span className={`font-serif text-2xl ${isBottleneck ? "text-[#B8863C]" : "text-[#1A1915]"}`}>{v.toFixed(1)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Situational composure */}
      {scores.situational_composure && scores.situational_composure.length > 0 && (
        <div className="mt-16 border-l-4 border-[#B8863C] pl-6 max-w-3xl" data-testid="results-situational-composure">
          <div className="eyebrow text-[#B8863C]">Situational Composure Detected</div>
          <p className="mt-2 text-base text-[#1A1915] leading-relaxed">The following trait(s) show high spread between weak and strong statements — the ability exists, a trigger switches it off. Name the trigger; the fix is at the threshold, not the ability.</p>
          <ul className="mt-4 space-y-2">
            {scores.situational_composure.map((s) => (
              <li key={s.trait} className="text-sm text-[#1A1915]">
                <span className="font-semibold">{s.trait}</span> — {s.inconsistency} inconsistency (spread {s.spread})
              </li>
            ))}
          </ul>
        </div>
      )}

      {scores.no_data_traits && scores.no_data_traits.length > 0 && (
        <div className="mt-8 border-l-4 border-[#78716C] pl-6 max-w-3xl">
          <div className="eyebrow">No-Data Traits</div>
          <p className="mt-2 text-sm text-[#1A1915]">You checked no statements for: <span className="font-semibold">{scores.no_data_traits.join(", ")}</span>. Silence here is itself a signal — look here.</p>
        </div>
      )}

      {/* Diagnosis */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-16" data-testid="results-diagnosis-section">
        <div className="md:col-span-4">
          <div className="eyebrow">Diagnosis</div>
          <h2 className="mt-4 font-serif text-4xl text-[#1A1915] leading-tight">Written verdict.</h2>
        </div>
        <div className="md:col-span-8 space-y-6" data-testid="results-diagnosis">
          {report.paragraphs.map((p, i) => (
            <p key={i} className="text-base text-[#1A1915] leading-relaxed">
              <span className="eyebrow mr-3 text-[#B8863C]">0{i + 1}</span>
              {p}
            </p>
          ))}
        </div>
      </div>

      {/* Action plan */}
      <div className="mt-16 border border-[#1A1915]" data-testid="results-action-plan">
        <div className="p-6 border-b border-[#1A1915]">
          <div className="eyebrow">Action Plan</div>
          <div className="font-serif text-3xl text-[#1A1915] mt-2">Prescription</div>
        </div>
        <div>
          {report.action_plan.map((a, i) => (
            <div key={i} className={`grid grid-cols-12 gap-4 p-6 ${i < report.action_plan.length - 1 ? "border-b border-[#1A1915]/20" : ""}`}>
              <div className="col-span-2 md:col-span-1 font-serif text-3xl text-[#1A1915]">0{a.priority}</div>
              <div className="col-span-7 md:col-span-9 text-base text-[#1A1915] leading-relaxed">{a.action}</div>
              <div className="col-span-3 md:col-span-2 text-xs uppercase tracking-widest text-[#78716C] text-right">{a.timeframe}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Deeper reading — omitted in print */}
      {!isPrint && (
      <div className="mt-16 border-t-4 border-[#1A1915] pt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="eyebrow">Deeper Reading</div>
          <h2 className="mt-4 font-serif text-3xl text-[#1A1915] leading-tight max-w-xl">Read the full model — the 19-section e-book behind this diagnosis.</h2>
        </div>
        <Link to="/model" className="btn-secondary" data-testid="results-read-model">
          <BookOpen className="mr-2 w-4 h-4" strokeWidth={1.5} /> Read the full model
        </Link>
      </div>
      )}

      <div className="mt-12 text-xs text-[#78716C] max-w-2xl">
        Report generated {new Date(data.created_at).toLocaleString()}. Retained for 24 months. Contact support with your purchase ID to request re-send or deletion.
      </div>
    </div>
  );
}
